
import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { CalendarIcon, ExternalLink, ArrowLeft } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { NEWS_SEARCH_QUERY } from "@/utils/constants";

interface NewsItem {
  title: string;
  summary: string;
  date: string;
  category: string;
  source: string;
  url: string;
}

const Archive = () => {
  const [newsData, setNewsData] = useState<NewsItem[]>([]);
  const [nextPage, setNextPage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const fetchNews = async (pageToken?: string | null) => {
    try {
      setLoading(true);
      const query = encodeURIComponent(NEWS_SEARCH_QUERY);
      const baseUrl = `https://newsdata.io/api/1/news?apikey=pub_70308430ef510e875c33c08ca89adaa745278&q=${query}&language=en&size=10`;
      const url = pageToken ? `${baseUrl}&page=${pageToken}` : baseUrl;
      
      const response = await fetch(url);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch news');
      }

      const data = await response.json();
      
      const formattedNews: NewsItem[] = data.results.map((article: any) => ({
        title: article.title,
        summary: article.description || "No summary available",
        date: new Date(article.pubDate).toISOString().split('T')[0],
        category: determineCategory(article.title),
        source: article.source_id,
        url: article.link
      }));

      setNewsData(prev => [...prev, ...formattedNews]);
      setNextPage(data.nextPage);
    } catch (error) {
      console.error('Error fetching news:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch archived news.",
      });
    } finally {
      setLoading(false);
    }
  };

  const determineCategory = (title: string): string => {
    const lowerTitle = title.toLowerCase();
    if (lowerTitle.includes('impact') || lowerTitle.includes('effect')) return 'Impact';
    if (lowerTitle.includes('research') || lowerTitle.includes('study')) return 'Research';
    if (lowerTitle.includes('free speech') || lowerTitle.includes('freedom')) return 'Free Speech';
    return 'AI & Society';
  };

  const loadMore = () => {
    if (nextPage) {
      fetchNews(nextPage);
    }
  };

  useEffect(() => {
    fetchNews();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <main className="container px-4 py-16 mx-auto">
        <div className="mb-8">
          <Link to="/" className="inline-flex items-center text-primary hover:text-primary/80">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Latest News
          </Link>
        </div>

        <div className="flex flex-col items-center justify-center space-y-8 mb-16">
          <img
            src="/lovable-uploads/fade160c-5745-42c2-9cc1-82d0f4d68794.png"
            alt="Causalitas"
            className="h-12 mb-8"
          />
          <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">
            AI Impact on Free Speech
          </h1>
          <p className="max-w-2xl mx-auto text-xl text-muted-foreground">
            Exploring how large language models influence freedom of expression and societal discourse
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 relative">
          {newsData.map((news, index) => (
            <div 
              key={index}
              className="transform transition-all duration-300 hover:-translate-y-2"
            >
              <div className="p-6 rounded-lg backdrop-blur-sm bg-white/5 border border-white/10 shadow-lg hover:bg-white/10 transition-all duration-300 h-full">
                <div className="flex items-center justify-between mb-4">
                  <Badge variant="secondary" className="text-xs font-medium">
                    {news.category}
                  </Badge>
                  <div className="flex items-center text-sm text-muted-foreground">
                    <CalendarIcon className="w-4 h-4 mr-1" />
                    {news.date}
                  </div>
                </div>
                <h3 className="text-xl font-semibold leading-tight tracking-tight mb-3">
                  {news.title}
                </h3>
                <p className="text-muted-foreground mb-4">{news.summary}</p>
                <a 
                  href={news.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-sm text-primary hover:text-primary/80 transition-colors"
                >
                  <span className="mr-1">Source: {news.source}</span>
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <Button
            onClick={loadMore}
            disabled={loading || !nextPage}
            variant="outline"
            size="lg"
            className="w-full md:w-auto"
          >
            {loading ? "Loading..." : nextPage ? "Load More Articles" : "No More Articles"}
          </Button>
        </div>

        <footer className="mt-16 py-8 border-t border-border">
          <nav className="flex flex-wrap justify-center gap-4 text-sm text-muted-foreground">
            <Link to="/privacy" className="hover:text-primary">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-primary">Terms of Service</Link>
            <Link to="/cookies" className="hover:text-primary">Cookie Policy</Link>
            <Link to="/imprint" className="hover:text-primary">Imprint</Link>
          </nav>
        </footer>
      </main>
    </div>
  );
};

export default Archive;
